import React, { useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useParticipation } from "../../hooks/use-participation";
import { SUBMIT_MISSION_SUBMISSION } from "../../api/participation";
import { SkeletonWave } from "../../../../components/common/skeleton-wave";

const SubmissionDetailPage = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const submissionIdNum = submissionId ? parseInt(submissionId) : 0;
  const participationId = searchParams.get("participationId")
    ? parseInt(searchParams.get("participationId")!)
    : null;

  const { participation, loading, error, refetch } = useParticipation(
    participationId || 0
  );

  // submission 찾기
  const submission =
    participation?.submissions?.find((s: any) => s.id === submissionIdNum) ||
    null;
  const step = submission?.step;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitSubmission] = useMutation(SUBMIT_MISSION_SUBMISSION);

  const handleClose = () => {
    navigate(-1);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // 미리보기 URL 생성
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenFilePicker = () => {
    if (
      !step?.proofType ||
      (step.proofType !== "PHOTO" && step.proofType !== "SCREENSHOT")
    ) {
      return;
    }

    // 파일 입력 요소 생성
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    // PHOTO 타입: 카메라 열기 (capture 속성 사용)
    // SCREENSHOT 타입: 갤러리 열기 (capture 속성 없음)
    if (step.proofType === "PHOTO") {
      input.capture = "environment"; // 뒷면 카메라
    }

    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        setSelectedFile(file);
        // 미리보기 URL 생성
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

    input.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAuthenticate = async () => {
    if (!submission || !step) {
      return;
    }

    // PHOTO 또는 SCREENSHOT 타입인 경우 파일이 필요함
    if (
      (step.proofType === "PHOTO" || step.proofType === "SCREENSHOT") &&
      !selectedFile
    ) {
      alert("인증 사진을 선택해주세요.");
      return;
    }

    setIsSubmitting(true);

    try {
      const proofData: any = {};

      // 파일이 있는 경우 proofData에 추가
      if (selectedFile) {
        proofData.imageFileName = selectedFile.name;
        proofData.imageFileType = selectedFile.type;
      }

      const { data } = await submitSubmission({
        variables: {
          submissionId: submissionIdNum,
          proofData,
          imageFile: selectedFile || null,
        },
      });

      if (data?.submitMissionSubmission?.success) {
        // 제출 성공 시 participation 데이터 다시 불러오기
        await refetch();
        alert("인증이 제출되었습니다.");
        navigate(-1);
      } else {
        alert(
          data?.submitMissionSubmission?.message ||
            "제출 중 오류가 발생했습니다."
        );
        setIsSubmitting(false);
      }
    } catch (err: any) {
      console.error("제출 오류:", err);
      const errorMessage =
        err.graphQLErrors?.[0]?.message ||
        err.message ||
        "제출 중 오류가 발생했습니다.";
      alert(errorMessage);
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <SkeletonWave className="pt-8" />
      </div>
    );
  }

  if (error || !participation || !submission) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-400 mb-4">
            {error
              ? "데이터를 불러오는 중 오류가 발생했습니다."
              : "인증 정보를 찾을 수 없습니다."}
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-4">
        <h1 className="flex-1 text-center text-lg font-semibold">
          이렇게 인증해 주세요!
        </h1>
        <button
          onClick={handleClose}
          className="w-6 h-6 flex items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="px-4 pb-24">
        {/* 미션 가이드 이미지 */}
        {step?.imageUrl && (
          <div className="mb-4">
            <img
              src={step.imageUrl}
              alt={step.title || `미션 ${submission.stepOrder} 가이드`}
              className="w-full rounded-lg object-cover"
            />
          </div>
        )}

        {/* 미션 단계 헤더 */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold text-lg">
            {submission.stepOrder}
          </div>
          <h2 className="text-lg font-semibold">
            {step?.title || `미션 ${submission.stepOrder}`}
          </h2>
        </div>

        {/* 인증 방법 안내 */}
        <div className="mb-6">
          {step?.guide && Array.isArray(step.guide) && (
            <div className="space-y-2 mb-3">
              {step.guide.map((guide: string, idx: number) => (
                <p key={idx} className="text-gray-300 leading-relaxed">
                  {guide}
                </p>
              ))}
            </div>
          )}

          {/* 인증 필수 조건 */}
          {step?.requirements &&
            Array.isArray(step.requirements) &&
            step.requirements.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="text-yellow-400">✨</span>
                  <h3 className="text-lg font-semibold">인증 필수 조건</h3>
                  <span className="text-yellow-400">✨</span>
                </div>
                <div className="space-y-3">
                  {step.requirements.map((req: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-gray-300 flex-1">{req}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          {/* 인증 방법 안내 (SCREENSHOT 또는 PHOTO 타입인 경우) */}
          {(step?.proofType === "SCREENSHOT" ||
            step?.proofType === "PHOTO") && (
            <div className="mt-3 p-3 bg-blue-900/30 rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm text-blue-300">
                  {step.proofType === "PHOTO"
                    ? "카메라로 직접 촬영해주세요."
                    : "갤러리에서 스크린샷을 선택해주세요."}
                </p>
              </div>
              {/* 파일 선택 버튼 */}
              <button
                onClick={handleOpenFilePicker}
                className="w-full mt-2 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                {step.proofType === "PHOTO" ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>카메라로 촬영</span>
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>갤러리에서 선택</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* 선택된 이미지 미리보기 */}
        {previewUrl && (
          <div className="mb-6">
            <div className="relative">
              <img
                src={previewUrl || undefined}
                alt="선택된 이미지"
                className="w-full rounded-lg object-cover max-h-96"
              />
              <button
                onClick={handleRemoveFile}
                className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-400 text-center">
                {selectedFile.name} (
                {((selectedFile?.size || 0) / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
        )}

        {/* 숨겨진 파일 입력 (브라우저 호환성을 위해 유지) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture={step?.proofType === "PHOTO" ? "environment" : undefined}
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* 하단 인증하기 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-black border-t border-gray-800">
        <button
          onClick={handleAuthenticate}
          disabled={
            isSubmitting ||
            ((step?.proofType === "PHOTO" ||
              step?.proofType === "SCREENSHOT") &&
              !selectedFile)
          }
          className={`w-full py-4 font-semibold rounded-lg ${
            isSubmitting ||
            ((step?.proofType === "PHOTO" ||
              step?.proofType === "SCREENSHOT") &&
              !selectedFile)
              ? "bg-gray-600 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {isSubmitting ? "제출 중..." : "인증하기"}
        </button>
      </div>
    </div>
  );
};

export default SubmissionDetailPage;
